import {
  WhitelistEntry,
  useAddWhitelistEntry,
  useRemoveWhitelistEntry,
  useWhitelist,
} from '@/services/agent/whitelist';
import { formatDateToShortString } from '@/utils/calendar';
import rem from '@/utils/rem';
import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Heading,
  Input,
  Radio,
  RadioGroup,
  Spinner,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { useState } from 'react';
import { HiChevronLeft, HiChevronRight } from 'react-icons/hi';

interface AccessWhitelistProps {
  onBack: () => void;
}

const AccessWhitelist = ({ onBack }: AccessWhitelistProps) => {
  const [entryType, setEntryType] = useState<string>('email');
  const [value, setValue] = useState('');
  const [note, setNote] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const toast = useToast();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const { data: entries = [], isLoading, error } = useWhitelist();
  const addMutation = useAddWhitelistEntry();
  const removeMutation = useRemoveWhitelistEntry();

  const handleAdd = async () => {
    const trimmed = value.trim().toLowerCase();
    if (!trimmed) {
      toast({ title: 'Value is required', status: 'error', duration: 3000, isClosable: true });
      return;
    }

    try {
      await addMutation.mutateAsync({ entry_type: entryType, value: trimmed, note: note.trim() });
      toast({
        title: 'Entry added',
        description: `${entryType === 'email' ? 'Email' : 'Domain'} "${trimmed}" has been whitelisted`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      setValue('');
      setNote('');
    } catch (err: any) {
      const message = err?.response?.data?.error || 'Failed to add entry';
      toast({ title: 'Failed to add entry', description: message, status: 'error', duration: 5000, isClosable: true });
    }
  };

  const handleRemove = async (entry: WhitelistEntry) => {
    try {
      await removeMutation.mutateAsync(entry.id);
      toast({
        title: 'Entry removed',
        description: `${entry.value} has been removed from the whitelist`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      const newTotal = entries.length - 1;
      const maxPage = Math.ceil(newTotal / pageSize);
      if (currentPage > maxPage && maxPage > 0) {
        setCurrentPage(maxPage);
      }
    } catch {
      toast({ title: 'Failed to remove entry', status: 'error', duration: 5000, isClosable: true });
    }
  };

  return (
    <Card marginBottom={rem(48)} background={bgColor} borderColor={borderColor} boxShadow="md">
      <CardHeader>
        <Flex alignItems="center" justifyContent="space-between">
          <Box>
            <Heading size="md">AI Access Whitelist</Heading>
            <Text marginTop={1} color="gray.500" fontSize="sm">
              Manage which emails and domains can access AI agent features
            </Text>
          </Box>
          <Button colorScheme="gray" onClick={onBack} size="sm">
            Back to Feature Flags
          </Button>
        </Flex>
      </CardHeader>
      <CardBody>
        {/* Add Entry Section */}
        <Box marginBottom={8}>
          <Heading marginBottom={4} size="sm">
            Add Entry
          </Heading>
          <Stack spacing={4}>
            <FormControl>
              <FormLabel fontSize="sm">Type</FormLabel>
              <RadioGroup onChange={setEntryType} value={entryType}>
                <HStack spacing={6}>
                  <Radio value="email">Email</Radio>
                  <Radio value="domain">Domain</Radio>
                </HStack>
              </RadioGroup>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">{entryType === 'email' ? 'Email address' : 'Domain name'}</FormLabel>
              <Input
                onChange={(e) => setValue(e.target.value)}
                placeholder={entryType === 'email' ? 'user@example.com' : 'example.com'}
                size="md"
                value={value}
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Note (optional)</FormLabel>
              <Input
                onChange={(e) => setNote(e.target.value)}
                placeholder="Reason for whitelisting"
                size="md"
                value={note}
              />
            </FormControl>
            <Box>
              <Button
                colorScheme="teal"
                isDisabled={!value.trim() || addMutation.isLoading}
                isLoading={addMutation.isLoading}
                onClick={handleAdd}
                size="sm"
              >
                Add to Whitelist
              </Button>
            </Box>
          </Stack>
        </Box>

        {/* Current Entries List */}
        <Box>
          <Heading marginBottom={4} size="sm">
            Current Entries ({entries.length})
          </Heading>
          {isLoading ? (
            <Flex alignItems="center" justifyContent="center" padding={8}>
              <Spinner size="xl" />
            </Flex>
          ) : error ? (
            <Text color="red.500" textAlign="center" paddingY={4}>
              Error loading whitelist
            </Text>
          ) : entries.length === 0 ? (
            <Text color="gray.500" textAlign="center" paddingY={4}>
              No whitelist entries. Add emails or domains above.
            </Text>
          ) : (
            <>
              <Box overflowX="auto">
                <Table variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Type</Th>
                      <Th>Value</Th>
                      <Th>Note</Th>
                      <Th>Date Added</Th>
                      <Th width="150px">Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {entries
                      .slice((currentPage - 1) * pageSize, currentPage * pageSize)
                      .map((entry: WhitelistEntry) => (
                        <Tr key={entry.id}>
                          <Td>
                            <Badge colorScheme={entry.entry_type === 'email' ? 'blue' : 'purple'}>
                              {entry.entry_type}
                            </Badge>
                          </Td>
                          <Td fontWeight="medium">{entry.value}</Td>
                          <Td color="gray.500" fontSize="sm">
                            {entry.note || '-'}
                          </Td>
                          <Td>{formatDateToShortString(entry.created_at)}</Td>
                          <Td>
                            <Button
                              colorScheme="red"
                              isDisabled={removeMutation.isLoading}
                              onClick={() => handleRemove(entry)}
                              size="sm"
                              variant="outline"
                            >
                              Remove
                            </Button>
                          </Td>
                        </Tr>
                      ))}
                  </Tbody>
                </Table>
              </Box>

              {/* Pagination */}
              {entries.length > pageSize && (
                <Flex alignItems="center" justifyContent="space-between" marginTop={4}>
                  <Text color="gray.500" fontSize="sm">
                    Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, entries.length)} of{' '}
                    {entries.length} entries
                  </Text>
                  <HStack spacing={2}>
                    <Button
                      isDisabled={currentPage === 1}
                      leftIcon={<HiChevronLeft />}
                      onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                      size="sm"
                      variant="outline"
                    >
                      Previous
                    </Button>
                    <Text fontSize="sm">
                      Page {currentPage} of {Math.ceil(entries.length / pageSize)}
                    </Text>
                    <Button
                      isDisabled={currentPage >= Math.ceil(entries.length / pageSize)}
                      onClick={() => setCurrentPage((prev) => Math.min(Math.ceil(entries.length / pageSize), prev + 1))}
                      rightIcon={<HiChevronRight />}
                      size="sm"
                      variant="outline"
                    >
                      Next
                    </Button>
                  </HStack>
                </Flex>
              )}
            </>
          )}
        </Box>
      </CardBody>
    </Card>
  );
};

export default AccessWhitelist;
